/**
 * 都市商务模板 - 首页
 *
 * 设计理念：深色大横幅搜索 + 紧凑分类网格 + 双列信息流
 * 与 sunset（极简搜索 + 横向图标 + 瀑布流）明显不同的排版骨架：
 * - 顶部：深藏青大横幅 Hero（白字大标题 + 搜索框 + 统计条）
 * - 分类：图标网格卡片（白底直角卡片）
 * - 最新：整齐的双列卡片网格（非瀑布流）
 * - 底部：快捷工具卡 + 空状态发布引导
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/urban/components/Navbar";
import UrbanFooter from "@/templates/urban/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function UrbanHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 12)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 16 })
      .then((data) => {
        setListings(data.list || []);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const pinnedListings = listings.filter(l => l.is_pinned);
  const normalListings = listings.filter(l => !l.is_pinned);

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <Navbar />

      {/* ======== 第一部分：深色大横幅 Hero + 搜索 ======== */}
      <section className="bg-gradient-to-br from-[#1B3A5C] via-[#23497A] to-[#2C5A8C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[#F5A94D] text-xs font-medium mb-5">
              <i className="ri-sparkling-line" />诚信 · 便捷 · 高效
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white leading-tight tracking-tight">
              找到你需要的
              <span className="block mt-1 bg-gradient-to-r from-[#F08A24] to-[#F5A94D] bg-clip-text text-transparent">
                信息与服务
              </span>
            </h1>
            <p className="text-gray-300 mt-4 text-sm sm:text-base">
              便捷发布 · 一键搜索 · 快速触达 · 值得信赖
            </p>

            {/* 搜索框 */}
            <form onSubmit={handleSearch} className="mt-8">
              <div className="flex items-center bg-white rounded-xl shadow-2xl shadow-black/20 overflow-hidden focus-within:ring-4 focus-within:ring-[#F08A24]/30 transition-shadow">
                <div className="flex-1 flex items-center pl-5">
                  <i className="ri-search-line text-[#1B3A5C] text-xl mr-3" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索你需要的信息..."
                    className="flex-1 py-4 sm:py-5 text-base text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                  />
                </div>
                <button
                  type="submit"
                  className="shrink-0 px-8 py-4 sm:py-5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold hover:from-[#E26A1B] hover:to-[#C95A12] transition-all text-sm sm:text-base"
                >
                  <i className="ri-search-line mr-1.5" />搜索
                </button>
              </div>
              {showSearchTip && (
                <div className="mt-3 inline-flex px-4 py-2.5 bg-[#E26A1B] text-white text-sm rounded-lg shadow-lg">
                  <i className="ri-error-warning-line mr-1" />{searchConfig.disabledMessage || "搜索功能已关闭"}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ======== 第二部分：分类图标网格 ======== */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-[#F08A24] rounded-sm inline-block" />
              服务分类
            </h2>
            <Link to="/category" className="text-sm text-[#1B3A5C] hover:text-[#F08A24] font-medium flex items-center gap-1 transition-colors">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingCategories ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse aspect-square bg-gray-200 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center justify-center gap-2.5 p-3 sm:p-4 bg-white rounded-xl border border-gray-100 hover:border-[#1B3A5C]/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 aspect-square"
                >
                  <div className="w-11 h-11 rounded-lg bg-[#1B3A5C]/5 flex items-center justify-center text-[#1B3A5C] group-hover:bg-[#1B3A5C] group-hover:text-white transition-colors">
                    <i className={`${cat.icon || 'ri-folder-line'} text-xl`} />
                  </div>
                  <span className="text-xs text-gray-600 font-medium text-center line-clamp-1">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ======== 第三部分：置顶推荐 1大 + 2小 ======== */}
      {!loadingListings && pinnedListings.length > 0 && (
        <section className="pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-[#F08A24] text-white rounded-md">
                <i className="ri-pushpin-line" />置顶推荐
              </span>
              <span className="text-xl font-bold text-gray-800">精选服务</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pinnedListings[0] && (
                <Link
                  to={`/listing/${pinnedListings[0].id}`}
                  className="sm:col-span-2 group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-[#1B3A5C]/20 transition-all"
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-1/2 aspect-square bg-[#1B3A5C]/5 overflow-hidden">
                      {pinnedListings[0].images?.[0] ? (
                        <img src={pinnedListings[0].images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-[#1B3A5C]/20 text-5xl" />
                        </div>
                      )}
                    </div>
                    <div className="sm:w-1/2 p-5 sm:p-8 flex flex-col justify-center bg-white">
                      <span className="text-xs text-[#F08A24] font-bold uppercase tracking-wide mb-2">
                        {pinnedListings[0].category?.name || "精选推荐"}
                      </span>
                      <h3 className="text-xl font-bold text-gray-800 group-hover:text-[#1B3A5C] transition-colors mb-3 line-clamp-2">
                        {pinnedListings[0].title}
                      </h3>
                      <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed mb-4">
                        {pinnedListings[0].content?.substring(0, 120) || "暂无描述"}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(pinnedListings[0].created_at)}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line" />{pinnedListings[0].view_count || 0} 浏览</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {pinnedListings.slice(1, 3).map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-[#1B3A5C]/20 transition-all"
                >
                  <div className="aspect-square bg-[#1B3A5C]/5 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-[#1B3A5C]/20 text-4xl" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-[#1B3A5C] transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                      <span>{item.category?.name || "未分类"}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ======== 第四部分：最新信息 双列网格 ======== */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-[#F08A24] rounded-sm inline-block" />
              最新发布
            </h2>
            <Link to="/category" className="text-sm text-[#1B3A5C] hover:text-[#F08A24] font-medium flex items-center gap-1 transition-colors">
              浏览全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="w-3/4 h-4 bg-gray-200 rounded" />
                    <div className="w-1/2 h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : normalListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {normalListings.slice(0, 8).map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-[#1B3A5C]/20 hover:-translate-y-0.5 transition-all"
                >
                  <div className="aspect-square overflow-hidden bg-[#1B3A5C]/5">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-[#1B3A5C]/20 text-3xl" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 leading-snug group-hover:text-[#1B3A5C] transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[11px] px-2 py-0.5 bg-[#1B3A5C]/5 text-[#1B3A5C] rounded font-medium">
                        {item.category?.name || "未分类"}
                      </span>
                      <span className="text-[11px] text-gray-400">{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-xl bg-[#1B3A5C]/5">
                <i className="ri-inbox-line text-2xl text-[#1B3A5C]/40" />
              </div>
              <p className="text-gray-500 font-medium">暂无信息</p>
              <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
            </div>
          )}
        </div>
      </section>

      {/* ======== 第五部分：快捷工具卡 ======== */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/publish"
              className="group flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-[#1B3A5C] to-[#2C5A8C] rounded-xl hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform">
                <i className="ri-add-line text-white text-2xl" />
              </div>
              <div className="text-center">
                <span className="text-sm font-bold text-white">发布信息</span>
                <p className="text-[11px] text-gray-300 mt-0.5">快速发布</p>
              </div>
            </Link>

            <Link
              to="/category"
              className="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-[#1B3A5C]/20 transition-all"
            >
              <div className="w-14 h-14 rounded-xl bg-[#1B3A5C]/5 flex items-center justify-center text-[#1B3A5C] group-hover:bg-[#1B3A5C] group-hover:text-white transition-colors shadow-sm">
                <i className="ri-grid-line text-2xl" />
              </div>
              <div className="text-center">
                <span className="text-sm font-bold text-gray-800">浏览分类</span>
                <p className="text-[11px] text-gray-400 mt-0.5">发现服务</p>
              </div>
            </Link>
          </div>

          {/* 列表为空时的发布引导 */}
          {!loadingListings && listings.length === 0 && (
            <div className="text-center mt-10">
              <Link
                to="/publish"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-xl hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-lg shadow-orange-500/30"
              >
                <i className="ri-add-line text-lg" />立即发布第一条信息
              </Link>
            </div>
          )}
        </div>
      </section>

      <UrbanFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
